/* QVF Flash Hero — multi-slide carousel
   Auto-rotates between hero slides, pauses on hover/focus,
   supports dot + arrow navigation, respects prefers-reduced-motion. */

class QvfFlashHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
    this.isPaused = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.qvf-flash-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.qvf-flash-hero__dot'));
    this.prevBtn = this.querySelector('.qvf-flash-hero__arrow--prev');
    this.nextBtn = this.querySelector('.qvf-flash-hero__arrow--next');

    if (this.slides.length <= 1) return;

    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 6000;

    // Honor reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) this.autoplay = false;

    this.bindEvents();
    if (this.autoplay) this.start();
  }

  bindEvents() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideTarget, 10);
        this.goTo(idx);
        this.restart();
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1);
        this.restart();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1);
        this.restart();
      });
    }

    // Pause on hover/focus
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());

    // Pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.resume();
    });

    // Touch swipe (mobile)
    let touchStartX = 0;
    this.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) < 50) return;
      if (dx < 0) this.goTo(this.currentIndex + 1);
      else this.goTo(this.currentIndex - 1);
      this.restart();
    }, { passive: true });
  }

  goTo(idx) {
    const total = this.slides.length;
    const next = ((idx % total) + total) % total;
    if (next === this.currentIndex) return;

    this.slides[this.currentIndex].classList.remove('is-active');
    this.dots[this.currentIndex]?.classList.remove('is-active');
    this.slides[next].classList.add('is-active');
    this.dots[next]?.classList.add('is-active');
    this.currentIndex = next;
  }

  start() {
    this.stop();
    this.timer = setInterval(() => {
      if (!this.isPaused) this.goTo(this.currentIndex + 1);
    }, this.interval);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    if (this.autoplay) this.start();
  }

  pause() { this.isPaused = true; }
  resume() { this.isPaused = false; }
}

if (!customElements.get('qvf-flash-hero-slider')) {
  customElements.define('qvf-flash-hero-slider', QvfFlashHeroSlider);
}
